import axios from 'axios';
import { stringifyUrl } from 'query-string';

import usStates from '@data/US_States';
import { ApiEndpoints } from '@utils/endpoints';
import { cond, startsWith, always, T, or } from 'ramda';

// Always use undefined to mean {sync: false, async: false}
export type ServiceAvailability =
	| undefined
	| { sync: true; async: true }
	| { sync: true; async: false }
	| { sync: false; async: true };

export interface IServiceAvailability {
	ed?: ServiceAvailability;
	hrt_e?: ServiceAvailability;
	hrt_t?: ServiceAvailability;
	prep?: ServiceAvailability;
	vc?: ServiceAvailability;
	mh_therapy?: ServiceAvailability;
	mh_medication?: ServiceAvailability;
	adhd?: ServiceAvailability;
}

export async function getServiceAvailability(state: string): Promise<IServiceAvailability> {
	const notAvailable: IServiceAvailability = {};
	return (await getAllStateServiceAvailability())[state] ?? notAvailable;
}

export async function getAllStateServiceAvailability(): Promise<{
	[key: string]: IServiceAvailability;
}> {
	return Object.fromEntries(
		(
			await axios.get<(IServiceAvailability & { stateCode: string })[]>(
				stringifyUrl({
					url: `${ApiEndpoints.public}/states`,
				}),
				{
					responseType: 'json',
				}
			)
		).data.map(({ stateCode, ...services }) => [
			stateCode,
			// Remove {sync: false, async: false} services
			Object.fromEntries(
				Object.entries(services).filter(
					([_, availability]) => availability.sync || availability.async
				)
			),
		])
	);
}

export interface IState {
	value: string;
	label: string;
}

export async function getStatesForProduct(productCode: string): Promise<IState[]> {
	const stateAvailability = await getAllStateServiceAvailability();
	return usStates.filter(
		(state) => (stateAvailability[state.value] as Record<string, any>)?.[productCode]
	);
}

export const getFlowName = cond<string, boolean, string>([
	[startsWith('hrt_e'), always('Estrogen')],
	[startsWith('hrt_t'), always('Testosterone')],
	[startsWith('mh'), always('Mental Health')],
	[startsWith('gaht'), always('Gender Affirming Hormone Therapy')],
	[(v: string) => or(startsWith('vhc', v), startsWith('vc', v)), always('Virtual Healthcare')],
	[(T, always(''))],
]);
